'use client';

import { Check, CirclePower, PlusIcon, Settings, User } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { useThemeProvider } from '@/utils/shared/ThemeContext';
import { themeLabel } from '@/shared/utils/theme';
import { Link } from 'react-router-dom';
import { ThemeIcon } from '@/shared/components/theme-icon';
import { paths } from '@/config/paths';

type UserMenuProps = {
  contexts: { name: string; id: string; active: boolean }[];
  onContextSwitch: (id: string) => void;
  onLogout: () => void;
  label?: string;
  sublabel?: string;
};

export function UserMenu({ contexts, onContextSwitch, label, sublabel, onLogout }: UserMenuProps) {
  const { currentTheme, changeCurrentTheme } = useThemeProvider();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button asChild variant={'ghost'} className="rounded-full size-8">
          <Avatar>
            <AvatarFallback>
              <User className="size-5" />
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60" side="bottom" sideOffset={10} align="end">
        <DropdownMenuLabel className="flex min-w-0 flex-col bg-gray-100 dark:bg-gray-700 p-4 items-center rounded-md mb-2">
          <Avatar className="mb-2">
            <AvatarFallback>
              <User className="size-8" />
            </AvatarFallback>
          </Avatar>
          <span className="truncate text-sm font-medium text-foreground">{label}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{sublabel}</span>
        </DropdownMenuLabel>

        <DropdownMenuGroup>
          <Link to={paths.user.root.getHref()}>
            <DropdownMenuItem>
              <Settings size={16} className="opacity-60" aria-hidden="true" />
              <span>Kontoinställningar</span>
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {contexts.map((ctx) => (
            <DropdownMenuItem onClick={() => onContextSwitch(ctx.id)} className="flex justify-between">
              <span className="truncate">{ctx.name}</span>
              {ctx.active && <Check size={16} className="text-primary" />}
            </DropdownMenuItem>
          ))}
          <Link to={paths.groups.register.getHref()}>
            <DropdownMenuItem>
              <PlusIcon size={16} className="opacity-60" aria-hidden="true" />
              <span>Skapa grupp</span>
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />

        <DropdownMenuGroup className="flex justify-between">
          <DropdownMenuItem onClick={() => changeCurrentTheme(currentTheme === 'light' ? 'dark' : 'light')}>
            <ThemeIcon />
            <span>{themeLabel(currentTheme as any)}</span>
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onClick={onLogout}>
            <span>Logga ut</span>
            <CirclePower size={16} aria-hidden="true" />
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
